import * as React from 'react';
import { useSelector } from 'react-redux';
import { Divider, Space } from 'antd';
import formatGaugeRingData from '@utils/overview/gaugeRing';
import { currentMetricsMetaSelector } from '@src/store/common/selectors';
import {
  BLUE_COLOR,
  RED_COLOR,
  GOOD_SESSIONS_NUMBER_COLOR,
  SPI_QUERY_METRICS,
  DEFAULT_METRIC,
} from '../../utils/spi';
import {
  GoodSessionsWidgetWrapper,
  ImpactedSessionsWidgetWrapper,
  WidgetTextWrapper,
  WidgetTitle,
  SessionsWrapper,
  StreamNumberWrapper,
} from './styled';
import { SESSION_WIDGET_CONFIG } from '@utils/kpi/spi';
import { useWidget } from '../../state/dashboard';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import RemoveWidget from '../../control/RemoveWidget';
import Drilldown from '../../control/Drilldown';
import { Retry } from '@src/components/common';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import useSelectMetric from '../../hooks/useSelectMetric';
import useFetchQoEData from './useFetchQoEData';
import { getHumanizedValueString } from '../../utils/format';

const ImpactedTimeWidget = ({ wid }) => {
  const widgetHandler = useWidget({ wid });
  const currentKpi = useCurrentKpiConfig();

  const { isFetching, isError, data, refetch, queued } = useFetchQoEData({
    wid,
    metrics: SPI_QUERY_METRICS,
    currentKpi,
  });
  const totalsData = data?.totals;

  const { createDashboard } = useDiagDashboard({
    metrics: SPI_QUERY_METRICS,
    defaultMetric: DEFAULT_METRIC.streams,
    title: widgetHandler.meta.title,
  });

  return (
    <WidgetCardWrapper
      wid={wid}
      title={widgetHandler.meta.title}
      style={{ gridRow: 'span 7' }}
      extra={
        <Space>
          <Drilldown createDashboard={createDashboard} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Retry
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        progress={true}
        queued={queued}
      >
        <div>
          <Sessions data={totalsData} />
        </div>
      </Retry>
    </WidgetCardWrapper>
  );
};

const Sessions = ({ data = {} }) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const badSessionsMetric = SESSION_WIDGET_CONFIG.bad;
  const goodSessionsValue = getHumanizedValueString(data, SESSION_WIDGET_CONFIG.good);
  const badSessionsValue = getHumanizedValueString(data, badSessionsMetric);
  const badSessionsPctValue = data[SESSION_WIDGET_CONFIG.badRate];

  const chartOption = React.useMemo(() => {
    const chartOption = formatGaugeRingData(
      [
        { value: 100 - badSessionsPctValue, name: 'Good Experience Time' },
        { value: badSessionsPctValue, name: 'Impacted Time' },
      ],
      [BLUE_COLOR, RED_COLOR],
    );

    return chartOption ? chartOption : {};
  }, [badSessionsPctValue]);

  React.useEffect(() => {
    if (instance) {
      instance.setOption(chartOption);
    }
  }, [instance, chartOption]);

  const selectMetric = useSelectMetric();

  const handleClick = () => {
    selectMetric(badSessionsMetric);
  };

  return (
    <SessionsWrapper>
      <BaseChart chartInstanceRefCb={chartInstanceRefCb} style={{ height: 80, width: 80 }} />
      <GoodSessionsWidgetWrapper>
        <WidgetTextWrapper>
          <WidgetTitle>{currentMetricsMeta[SESSION_WIDGET_CONFIG.good].displayName}</WidgetTitle>
          <StreamNumberWrapper style={{ color: GOOD_SESSIONS_NUMBER_COLOR, display: 'flex' }}>
            <span style={{ fontSize: 16 }}>{goodSessionsValue}</span>
          </StreamNumberWrapper>
        </WidgetTextWrapper>
      </GoodSessionsWidgetWrapper>
      <Divider type="vertical" style={{ height: 60 }} />
      <ImpactedSessionsWidgetWrapper onClick={handleClick}>
        <WidgetTextWrapper>
          <WidgetTitle>{currentMetricsMeta[badSessionsMetric].displayName}</WidgetTitle>
          <StreamNumberWrapper style={{ color: RED_COLOR, display: 'flex' }}>
            <span style={{ fontSize: 16 }}>{badSessionsValue}</span>
          </StreamNumberWrapper>
        </WidgetTextWrapper>
      </ImpactedSessionsWidgetWrapper>
    </SessionsWrapper>
  );
};

export default ImpactedTimeWidget;
